export const AI_EXHIBIT_21_COPY = Object.freeze({
  en: {
    historyDrivers: {
      statusAvailable: "AVAILABLE",
      statusConstrained: "CONSTRAINED",
      historicalExampleEyebrow: "A HISTORICAL EXAMPLE",
      changedWorldEyebrow: "WHAT CHANGES IN THE REAL WORLD?",
      multiConstraintExample:
        "With several constraints at once, an idea may work in a small demonstration and still be too slow, too brittle, or too limited to become a useful large-scale system.",
      allTitle: "When the ingredients come together",
      allExample:
        "A useful example is the 2012 deep-learning breakthrough in image recognition: years of neural-network research, the large ImageNet dataset, GPU computing, and sustained research effort came together at the right time.",
      single: {
        algorithms: {
          title: "Without the right algorithms, resources are not enough",
          example:
            "Imagine having enormous image collections and fast GPUs, but no effective way to train a model that can use them. New architectures and training methods changed what all that data and computing could accomplish.",
          anchor:
            "Real-world anchor: convolutional neural networks helped transform image recognition; the 2017 Transformer later changed language modeling.",
        },
        data: {
          title: "Without enough useful data, the model has less to learn from",
          example:
            "A powerful learning method still needs examples. Large labeled collections such as ImageNet helped researchers train and compare image-recognition systems at a scale that had been difficult before.",
          anchor:
            "Real-world anchor: better data is not just 'more data'—coverage, labeling, quality, and representativeness matter too.",
        },
        compute: {
          title: "Without enough computing power, a good idea may be impractical",
          example:
            "Deep neural networks can require huge numbers of numerical operations. GPU computing made many large experiments dramatically more practical and helped accelerate deep learning in the 2010s.",
          anchor:
            "Real-world anchor: AlexNet's 2012 system was trained using GPUs rather than relying only on traditional CPU computing.",
        },
        investment: {
          title: "Ideas also need people, infrastructure, and sustained effort",
          example:
            "Research papers do not turn themselves into large working systems. Researchers, engineers, specialized chips, data infrastructure, testing, and product development all require resources and time.",
          anchor:
            "Real-world anchor: modern frontier systems require substantial engineering and computing infrastructure in addition to algorithms.",
        },
      },
      combinations: {
        "compute,data": {
          title: "Good idea, but little fuel",
          example:
            "Suppose researchers have a clever algorithm but not enough training examples and not enough computing power. The idea may work in a small demonstration yet fail to scale into a capable system.",
        },
        "algorithms,compute": {
          title: "Lots of examples, but a weak learning engine",
          example:
            "A huge dataset cannot compensate forever for ineffective learning methods or insufficient computing. The system may have information available but no practical way to extract useful patterns from it.",
        },
        "algorithms,data": {
          title: "Powerful hardware alone does not create intelligence",
          example:
            "A room full of fast computers is not an AI system by itself. Without effective algorithms and useful examples, the hardware has no meaningful learning problem to solve.",
        },
        "investment": {
          title: "A promising laboratory result can remain a laboratory result",
          example:
            "Without sustained resources, teams may struggle to reproduce results, build infrastructure, test safety and reliability, or turn a prototype into something people can actually use.",
        },
      },
    },

    trainMachine: {
      featureA: "How blue?",
      featureB: "How round?",
      bigQuestion: "What does the machine think?",
      untrainedTitle: "I don't know yet.",
      untrainedBody:
        "I have not learned from the examples, so I am basically guessing.",
      leaningZorb: "I'm leaning toward Zorb.",
      leaningNot: "I'm leaning toward Not Zorb.",
      confidentZorb: "I think this is a Zorb.",
      confidentNot: "I think this is Not a Zorb.",
      zorbConfidence: "Zorb",
      notConfidence: "Not Zorb",
      simpleStory:
        "The important part is not the percentage. Watch the machine move from guessing toward a rule that better matches the examples.",
      insideTitle: "Curious? What changed inside?",
      insideBody:
        "The model stores numerical parameters. Training changes those numbers. The visitor does not need to understand the numbers to understand the learning cycle.",
      trainPrompt: "Teach with the examples",
      afterStep:
        "I made predictions, compared them with the correct labels, measured my error, and changed my internal numbers a little.",
      improved:
        "My error is getting smaller. The examples are pushing me toward a clearer boundary between the two groups.",
      surpriseButton: "Surprise the machine",
      surpriseTitle: "Uh-oh. A blue creature that is NOT a Zorb.",
      surpriseCorrect: "Correct label: Not Zorb",
      surpriseBefore:
        "The original examples made 'blue' look like a very useful shortcut. The machine had never seen a blue Not-Zorb before.",
      surpriseFooled:
        "The machine gets fooled. This is a simple example of a model learning a shortcut that worked on its training examples but did not capture the whole rule.",
      surpriseNotFooled:
        "The machine is uncertain here, but the new example still teaches it something the original training set did not show.",
      addSurprise: "Add this example to training data",
      surpriseAdded:
        "Now the machine has evidence that 'blue' does not always mean Zorb. Train again and watch its prediction change.",
      retrain: "Train 10 more steps",
      learnedFromSurprise:
        "The new example changed what the model learned. Better coverage in training data can reduce shortcut learning—although real AI systems can have much more complicated failure modes.",
      labelsTitle: "The answers we give the machine",
      legendZorb: "Blue + round examples labeled Zorb",
      legendNot: "Red + angular examples labeled Not Zorb",
      testCreature: "New creature the machine has never seen",
      mathSecondary:
        "Weights and bias are shown as a secondary layer for curious visitors and educators.",
    },
  },

  es: {
    historyDrivers: {
      statusAvailable: "DISPONIBLE",
      statusConstrained: "LIMITADO",
      historicalExampleEyebrow: "UN EJEMPLO HISTÓRICO",
      changedWorldEyebrow: "¿QUÉ CAMBIA EN EL MUNDO REAL?",
      multiConstraintExample:
        "Con varias restricciones a la vez, una idea puede funcionar en pequeña escala y aun así ser demasiado lenta, demasiado frágil o demasiado limitada para convertirse en un sistema útil.",
      allTitle: "Cuando los ingredientes se combinan",
      allExample:
        "Un ejemplo útil es el avance del aprendizaje profundo en reconocimiento de imágenes en 2012: años de investigación en redes neuronales, el gran conjunto ImageNet, computación con GPU y un esfuerzo sostenido de investigación coincidieron en el momento adecuado.",
      single: {
        algorithms: {
          title: "Sin buenos algoritmos, los recursos no bastan",
          example:
            "Imagina enormes colecciones de imágenes y GPU rápidas, pero sin una forma eficaz de entrenar un modelo que pueda aprovecharlas. Nuevas arquitecturas y métodos cambiaron lo que esos recursos podían lograr.",
          anchor:
            "Ejemplo real: las redes convolucionales transformaron el reconocimiento de imágenes; el Transformer de 2017 cambió después el modelado del lenguaje.",
        },
        data: {
          title: "Sin suficientes datos útiles, hay menos de qué aprender",
          example:
            "Un método potente necesita ejemplos. Grandes colecciones etiquetadas como ImageNet ayudaron a entrenar y comparar sistemas de reconocimiento de imágenes a una escala antes difícil.",
          anchor:
            "Ejemplo real: mejores datos no significan solo más datos; también importan cobertura, calidad, etiquetas y representatividad.",
        },
        compute: {
          title: "Sin suficiente computación, una buena idea puede ser impráctica",
          example:
            "Las redes profundas pueden requerir enormes cantidades de operaciones numéricas. Las GPU hicieron mucho más prácticos muchos experimentos grandes y aceleraron el aprendizaje profundo.",
          anchor:
            "Ejemplo real: el sistema AlexNet de 2012 fue entrenado usando GPU.",
        },
        investment: {
          title: "Las ideas también necesitan personas, infraestructura y esfuerzo sostenido",
          example:
            "Los artículos científicos no se convierten solos en grandes sistemas. Investigación, ingeniería, chips, infraestructura de datos, pruebas y desarrollo requieren recursos y tiempo.",
          anchor:
            "Ejemplo real: los sistemas de frontera modernos requieren una infraestructura considerable además de algoritmos.",
        },
      },
      combinations: {
        "compute,data": {
          title: "Buena idea, pero poco combustible",
          example:
            "Un algoritmo inteligente con pocos ejemplos y poca computación puede funcionar en una demostración pequeña pero no escalar a un sistema capaz.",
        },
        "algorithms,compute": {
          title: "Muchos ejemplos, pero un motor de aprendizaje débil",
          example:
            "Un conjunto enorme de datos no compensa indefinidamente métodos ineficaces o computación insuficiente.",
        },
        "algorithms,data": {
          title: "El hardware potente por sí solo no crea inteligencia",
          example:
            "Una sala llena de computadoras rápidas no es IA por sí misma. Sin algoritmos eficaces y ejemplos útiles, no existe un problema de aprendizaje significativo.",
        },
        "investment": {
          title: "Un resultado prometedor puede quedarse en el laboratorio",
          example:
            "Sin recursos sostenidos, puede ser difícil reproducir resultados, construir infraestructura, probar fiabilidad o convertir un prototipo en algo utilizable.",
        },
      },
    },

    trainMachine: {
      featureA: "¿Qué tan azul?",
      featureB: "¿Qué tan redondo?",
      bigQuestion: "¿Qué piensa la máquina?",
      untrainedTitle: "Todavía no lo sé.",
      untrainedBody:
        "Aún no he aprendido de los ejemplos, así que básicamente estoy adivinando.",
      leaningZorb: "Me inclino por Zorb.",
      leaningNot: "Me inclino por No Zorb.",
      confidentZorb: "Creo que es un Zorb.",
      confidentNot: "Creo que no es un Zorb.",
      zorbConfidence: "Zorb",
      notConfidence: "No Zorb",
      simpleStory:
        "Lo importante no es el porcentaje. Observa cómo la máquina pasa de adivinar a una regla que coincide mejor con los ejemplos.",
      insideTitle: "¿Curioso? ¿Qué cambió por dentro?",
      insideBody:
        "El modelo guarda parámetros numéricos. El entrenamiento cambia esos números. No necesitas entenderlos para comprender el ciclo de aprendizaje.",
      trainPrompt: "Enseñar con los ejemplos",
      afterStep:
        "Hice predicciones, las comparé con las etiquetas correctas, medí mi error y cambié un poco mis números internos.",
      improved:
        "Mi error está disminuyendo. Los ejemplos me empujan hacia una frontera más clara entre los dos grupos.",
      surpriseButton: "Sorprende a la máquina",
      surpriseTitle: "¡Oh! Una criatura azul que NO es Zorb.",
      surpriseCorrect: "Etiqueta correcta: No Zorb",
      surpriseBefore:
        "Los ejemplos originales hicieron que 'azul' pareciera un atajo muy útil. La máquina nunca había visto un No-Zorb azul.",
      surpriseFooled:
        "La máquina cae en la trampa. Es un ejemplo sencillo de aprender un atajo que funcionaba con los datos de entrenamiento pero no captaba toda la regla.",
      surpriseNotFooled:
        "La máquina duda, pero el nuevo ejemplo todavía le enseña algo que el conjunto original no mostraba.",
      addSurprise: "Añadir este ejemplo a los datos",
      surpriseAdded:
        "Ahora la máquina tiene evidencia de que 'azul' no siempre significa Zorb. Entrena otra vez y observa cómo cambia la predicción.",
      retrain: "Entrenar 10 pasos más",
      learnedFromSurprise:
        "El nuevo ejemplo cambió lo aprendido. Una mejor cobertura de los datos puede reducir los atajos, aunque los sistemas reales tienen fallos mucho más complejos.",
      labelsTitle: "Las respuestas que damos a la máquina",
      legendZorb: "Ejemplos azules + redondos etiquetados Zorb",
      legendNot: "Ejemplos rojos + angulares etiquetados No Zorb",
      testCreature: "Criatura nueva que la máquina nunca ha visto",
      mathSecondary:
        "Los pesos y el sesgo se muestran como una capa secundaria para visitantes curiosos y educadores.",
    },
  },
});

export function getAIExhibit21Copy(language = "en") {
  return AI_EXHIBIT_21_COPY[language] || AI_EXHIBIT_21_COPY.en;
}
